import { computed, DestroyRef, effect, Injector, signal } from '@angular/core';
import type { FinanceCommand, SubmitToolResult } from '@app/platform/ai-session-protocol';
import type { IdleControlPort } from '@app/ui/shared';
import { AiSessionRelayClient, AiSessionRelayError } from './ai-session-relay.client';
import { AiSessionSettingsService } from './ai-session-settings.service';
import { FinanceToolRegistry } from './finance-tool.registry';
import type { AiSessionHostPort } from './tokens';

export type AiSessionStatus = 'idle' | 'starting' | 'waiting' | 'paired' | 'recently-active' | 'expired' | 'error';
export type CommandActivityStatus = 'success' | 'failed';

export interface CommandActivity {
  requestId: string;
  name: string;
  status: CommandActivityStatus;
  durationMs: number;
  finishedAt: number;
}

export class AiSessionCoordinator {
  private readonly sessionKey = signal<string | null>(null);
  private readonly mcpKey = signal<string | null>(null);
  private readonly pairingCode = signal<string | null>(null);
  private readonly paired = signal(false);
  private readonly recentActivity = signal<CommandActivity[]>([]);
  private pollAbortController?: AbortController;
  private startPending = false;

  readonly status = signal<AiSessionStatus>('idle');
  readonly errorMessage = signal<string | null>(null);
  readonly roomCode = computed(() => this.pairingCode());
  readonly formattedRoomCode = computed(() => {
    const code = this.roomCode();
    return code ? `${code.slice(0, 3)} ${code.slice(3)}` : null;
  });
  readonly expiresAt = computed(() => {
    const now = Date.now();
    return new Date(now + 30 * 60 * 1000).toISOString();
  });
  readonly activityLog = computed(() => this.recentActivity());
  readonly active = computed(() => this.sessionKey() !== null);
  readonly busy = computed(() => this.active() || this.status() === 'starting');

  constructor(
    private readonly host: AiSessionHostPort,
    private readonly settings: AiSessionSettingsService,
    private readonly relay: AiSessionRelayClient,
    private readonly tools: FinanceToolRegistry,
    injector: Injector,
    destroyRef: DestroyRef,
    idle: IdleControlPort,
  ) {
    const releaseIdle = idle.registerBusySource(this.busy);
    effect(
      () => {
        if (this.sessionKey() && (!this.host.isAuthorized() || !this.host.isLeader())) {
          void this.stop();
        }
      },
      { injector },
    );
    destroyRef.onDestroy(() => {
      releaseIdle?.();
      void this.stop();
    });
  }

  async validateAndSaveRelayUrl(value: string): Promise<void> {
    const normalized = this.settings.normalizeRelayUrl(value);
    await this.relay.checkHealth(normalized);
    this.settings.saveRelayUrl(normalized);
  }

  async start(): Promise<void> {
    if (this.sessionKey() || this.startPending) return;
    if (!this.host.isAuthorized() || !this.host.isLeader()) {
      this.fail('screens.ai_session.notifications.must_unlock_leader');
      return;
    }
    const baseUrl = this.settings.relayUrl();
    if (!baseUrl) {
      this.fail('screens.ai_session.notifications.configure_relay_first');
      return;
    }

    this.startPending = true;
    this.status.set('starting');
    this.errorMessage.set(null);
    try {
      if (!(await this.host.confirmPassword())) {
        this.status.set('idle');
        return;
      }
      await this.relay.checkHealth(baseUrl);
      const sessionKey = this.generateSessionKey();
      const mcpKey = this.generateSessionKey();
      const session = await this.relay.createSession(baseUrl, sessionKey, mcpKey);
      this.sessionKey.set(sessionKey);
      this.mcpKey.set(mcpKey);
      this.paired.set(false);
      this.pairingCode.set(session.roomCode);
      this.status.set('waiting');
      void this.pollLoop(baseUrl, sessionKey, mcpKey);
    } catch (error) {
      this.fail(this.messageFrom(error));
    } finally {
      this.startPending = false;
    }
  }

  async stop(): Promise<void> {
    const sessionKey = this.sessionKey();
    const baseUrl = this.settings.relayUrl();

    this.pollAbortController?.abort();
    this.pollAbortController = undefined;
    this.sessionKey.set(null);
    this.mcpKey.set(null);
    this.paired.set(false);
    this.pairingCode.set(null);
    this.recentActivity.set([]);
    this.status.set('idle');
    this.errorMessage.set(null);

    if (!sessionKey || !baseUrl) {
      return;
    }

    try {
      await this.relay.closeSession(baseUrl, sessionKey);
    } catch (error) {
      if (!(error instanceof AiSessionRelayError && error.status === 404)) {
        console.warn('Failed to close relay session', error);
      }
    }
  }

  private generateSessionKey(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private async pollLoop(baseUrl: string, sessionKey: string, mcpKey: string): Promise<void> {
    while (this.sessionKey() === sessionKey) {
      const abortController = new AbortController();
      this.pollAbortController = abortController;
      try {
        const batch = await this.relay.poll(baseUrl, sessionKey, abortController.signal);
        this.errorMessage.set(null);
        if (!batch) continue;
        if (batch.commands.length === 0) {
          if (!batch.paired) {
            this.status.set('waiting');
          } else if (this.status() !== 'recently-active') {
            this.paired.set(true);
            this.pairingCode.set(null);
            this.status.set('paired');
          }
          continue;
        }
        this.paired.set(true);
        this.pairingCode.set(null);
        this.status.set('paired');
        await Promise.all(batch.commands.map((command) => this.executeCommand(baseUrl, sessionKey, command)));
        this.status.set('recently-active');
      } catch (error) {
        if (abortController.signal.aborted) return;
        this.status.set('error');
        this.errorMessage.set(this.messageFrom(error));
        if (error instanceof AiSessionRelayError && error.status === 404) {
          try {
            await this.recoverSession(baseUrl, sessionKey, mcpKey);
          } catch {
            // Keep polling; the relay may still be restarting.
          }
        }
        // Always continue polling - errors are transient (network, server issues, etc.)
        continue;
      }
    }
  }

  private async recoverSession(baseUrl: string, sessionKey: string, mcpKey: string): Promise<void> {
    if (this.sessionKey() !== sessionKey || this.mcpKey() !== mcpKey) return;
    const wasPaired = this.paired();
    const session = await this.relay.createSession(baseUrl, sessionKey, mcpKey, wasPaired);
    if (this.sessionKey() !== sessionKey || this.mcpKey() !== mcpKey) return;
    this.pairingCode.set(wasPaired ? null : session.roomCode);
    this.status.set(wasPaired ? 'paired' : 'waiting');
    this.errorMessage.set(null);
  }

  private async executeCommand(baseUrl: string, sessionKey: string, command: FinanceCommand): Promise<void> {
    const startedAt = Date.now();
    let activityStatus: CommandActivityStatus = 'failed';
    let result: SubmitToolResult;
    if (!this.host.isAuthorized() || !this.host.isLeader()) {
      result = {
        requestId: command.requestId,
        ok: false,
        error: { code: 'FINANCE_LOCKED', message: 'Finance is locked or not the leader tab' },
      };
    } else {
      try {
        result = {
          requestId: command.requestId,
          ok: true,
          result: await this.tools.execute(command),
        };
        activityStatus = 'success';
      } catch (error) {
        console.error('Failed to execute command', error, command);
        result = {
          requestId: command.requestId,
          ok: false,
          error: { code: 'LOCAL_EXECUTION_FAILED', message: 'Finance could not execute the read-only query' },
        };
      }
    }
    try {
      await this.relay.submitResult(baseUrl, sessionKey, result);
      activityStatus = result.ok ? 'success' : 'failed';
    } finally {
      this.recentActivity.update((entries) =>
        [
          {
            requestId: command.requestId,
            name: command.name,
            status: activityStatus,
            durationMs: Date.now() - startedAt,
            finishedAt: Date.now(),
          },
          ...entries,
        ].slice(0, 5),
      );
    }
  }

  private fail(message: string): void {
    this.status.set('error');
    this.errorMessage.set(message);
  }

  private messageFrom(error: unknown): string {
    return error instanceof Error ? error.message : 'screens.ai_session.notifications.session_failed';
  }
}
